import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Car,
  Building2,
  Users,
  Zap,
  Droplets,
  Home,
  Bell,
  AlertTriangle,
  Calendar,
  RefreshCw,
  TrendingUp,
  Clock,
  CheckCircle2,
  XCircle,
  PieChart
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Use relative URL for API calls - works for both preview and deployed versions
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const { token, isAdmin } = useAuth();
  const [summary, setSummary] = useState(null);
  const [expiringItems, setExpiringItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingExpiry, setCheckingExpiry] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, expiringRes] = await Promise.all([
        axios.get(`${API}/dashboard/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/dashboard/expiring`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSummary(summaryRes.data);
      setExpiringItems(expiringRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      // Show more detailed error in development
      if (error.response) {
        toast.error(`خطأ: ${error.response.status} - ${error.response.statusText}`);
      } else if (error.request) {
        toast.error('خطأ في الاتصال بالخادم');
      } else {
        toast.error('خطأ في تحميل البيانات');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleCheckExpiry = async () => {
    setCheckingExpiry(true);
    try {
      const response = await axios.post(`${API}/check-expiry`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('خطأ في فحص تواريخ الانتهاء');
    } finally {
      setCheckingExpiry(false);
    }
  };

  // بطاقات الأصول الرئيسية (سيارات وعقارات فقط)
  const assetCards = [
    { title: 'السيارات', value: summary?.cars || 0, icon: Car, href: '/cars', color: '#3B82F6', bgColor: '#EFF6FF' },
    { title: 'الصكوك العقارية', value: summary?.real_estate || 0, icon: Building2, href: '/real-estate', color: '#10B981', bgColor: '#ECFDF5' },
    { title: 'الإيجارات', value: summary?.rentals || 0, icon: Home, href: '/rentals', color: '#EF4444', bgColor: '#FEF2F2' },
  ];
  
  // بطاقات المرافق (منفصلة عن الأصول)
  const utilityCards = [
    { title: 'الكهرباء', value: summary?.electricity || 0, icon: Zap, href: '/electricity', color: '#F59E0B', bgColor: '#FFFBEB' },
    { title: 'المياه', value: summary?.water || 0, icon: Droplets, href: '/water', color: '#06B6D4', bgColor: '#ECFEFF' },
  ];
  
  // بطاقة العمالة منفصلة
  const workersCard = { title: 'العمالة', value: summary?.workers || 0, icon: Users, href: '/workers', color: '#8B5CF6', bgColor: '#F5F3FF' };
  
  // جميع البطاقات للعرض
  const statsCards = [...assetCards, workersCard, ...utilityCards];

  const getDaysRemaining = (dateStr) => {
    const today = new Date();
    const expiry = new Date(dateStr);
    const diff = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getStatusBadge = (days) => {
    if (days <= 7) return <Badge variant="destructive" className="text-xs">عاجل</Badge>;
    if (days <= 14) return <Badge className="bg-amber-500 text-white text-xs">قريب</Badge>;
    return <Badge variant="secondary" className="text-xs">تنبيه</Badge>;
  };

  // إجمالي الأصول (سيارات + عقارات فقط - بدون الكهرباء والمياه والإيجارات)
  const totalAssets = (summary?.cars || 0) + (summary?.real_estate || 0);
  
  // حساب الأصول الفريدة التي لديها تنبيهات (بدون تكرار)
  // منتهية (أقل من 1 يوم)
  const expiredAssetIds = new Set(expiringItems.filter(item => getDaysRemaining(item.expiry_date) < 1).map(item => item.item_id));
  // عاجل (1-7 أيام)
  const urgentAssetIds = new Set(expiringItems.filter(item => {
    const days = getDaysRemaining(item.expiry_date);
    return days >= 1 && days <= 7;
  }).map(item => item.item_id));
  // تنتهي قريباً (8-30 يوم)
  const upcomingAssetIds = new Set(expiringItems.filter(item => {
    const days = getDaysRemaining(item.expiry_date);
    return days > 7 && days <= 30;
  }).map(item => item.item_id));
  
  // عدد الأصول الفريدة المنتهية
  const expiredAssets = expiredAssetIds.size;
  // عدد الأصول العاجلة (1-7 أيام)
  const urgentAssets = urgentAssetIds.size;
  // عدد الأصول التي تنتهي قريباً (8-30 يوم)
  const upcomingAssets = upcomingAssetIds.size;
  // الأصول السارية = الإجمالي - المنتهية - العاجلة - القريبة
  const activeItems = Math.max(0, totalAssets - expiredAssets - urgentAssets - upcomingAssets);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold" style={{ color: '#1a365d' }}>لوحة التحكم</h1>
          <p className="text-slate-500 text-sm">نظرة عامة على جميع الأصول</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
        {statsCards.map((stat) => (
          <Link key={stat.href} to={stat.href} data-testid={`stat-card-${stat.href.replace('/', '')}`}>
            <Card className="card-hover border-0 shadow-sm hover:shadow-md h-full">
              <CardContent className="p-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-2"
                  style={{ backgroundColor: stat.bgColor }}
                >
                  <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                </div>
                <p className="text-xl font-bold number" style={{ color: '#1a365d' }}>{stat.value}</p>
                <p className="text-xs text-slate-500">{stat.title}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Main Content - 2 columns for larger view */}
      <div className="grid lg:grid-cols-2 gap-6">
        
        {/* Assets Distribution Chart */}
        <Card className="border-0 shadow-sm" data-testid="assets-chart">
          <CardHeader className="pb-3 pt-4 px-6">
            <CardTitle className="flex items-center gap-2 text-lg" style={{ color: '#1a365d' }}>
              <PieChart className="w-5 h-5" style={{ color: '#D4AF37' }} />
              توزيع الأصول
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {/* Simple Pie Chart using CSS */}
            <div className="relative w-48 h-48 mx-auto mb-4">
              <svg viewBox="0 0 100 100" className="transform -rotate-90">
                {totalAssets > 0 ? (
                  <>
                    {assetCards.reduce((acc, stat, index) => {
                      const percentage = (stat.value / totalAssets) * 100;
                      const previousTotal = acc.total;
                      const strokeDasharray = `${percentage} ${100 - percentage}`;
                      const strokeDashoffset = -previousTotal;
                      
                      acc.elements.push(
                        <circle
                          key={stat.href}
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke={stat.color}
                          strokeWidth="20"
                          strokeDasharray={strokeDasharray}
                          strokeDashoffset={strokeDashoffset}
                          className="transition-all duration-500"
                        />
                      );
                      acc.total += percentage;
                      return acc;
                    }, { elements: [], total: 0 }).elements}
                  </>
                ) : (
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#e2e8f0" strokeWidth="20" />
                )}
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-3xl font-bold number" style={{ color: '#1a365d' }}>{totalAssets}</p>
                  <p className="text-sm text-slate-500">إجمالي الأصول</p>
                </div>
              </div>
            </div>
            
            {/* Legend - فقط الأصول */}
            <div className="grid grid-cols-2 gap-3">
              {assetCards.map((stat) => (
                <div key={stat.href} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: stat.color }}></div>
                  <span className="text-sm text-slate-600 flex-1">{stat.title}</span>
                  <span className="text-sm font-bold number" style={{ color: stat.color }}>{stat.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Status Summary */}
        <Card className="border-0 shadow-sm" data-testid="status-summary">
          <CardHeader className="pb-3 pt-4 px-6">
            <CardTitle className="flex items-center gap-2 text-lg" style={{ color: '#1a365d' }}>
              <TrendingUp className="w-5 h-5" style={{ color: '#D4AF37' }} />
              حالة الأصول
            </CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            {/* Status Bars */}
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                    <span className="text-base font-medium text-slate-700">أصول سارية</span>
                  </div>
                  <span className="text-2xl font-bold number text-emerald-600">{activeItems}</span>
                </div>
                <div className="h-3 rounded-full bg-emerald-100 overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 transition-all duration-500"
                    style={{ width: totalAssets > 0 ? `${(activeItems / totalAssets) * 100}%` : '0%' }}
                  />
                </div>
              </div>
              
              <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Clock className="w-6 h-6 text-amber-500" />
                    <span className="text-base font-medium text-slate-700">تنتهي قريباً (8-30 يوم)</span>
                  </div>
                  <span className="text-2xl font-bold number text-amber-600">{upcomingAssets}</span>
                </div>
                <div className="h-3 rounded-full bg-amber-100 overflow-hidden">
                  <div 
                    className="h-full bg-amber-500 transition-all duration-500"
                    style={{ width: totalAssets > 0 ? `${(upcomingAssets / totalAssets) * 100}%` : '0%' }}
                  />
                </div>
              </div>
              
              <div className="p-4 rounded-xl bg-orange-50 border border-orange-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-6 h-6 text-orange-500" />
                    <span className="text-base font-medium text-slate-700">عاجل (1-7 أيام)</span>
                  </div>
                  <span className="text-2xl font-bold number text-orange-600">{urgentAssets}</span>
                </div>
                <div className="h-3 rounded-full bg-orange-100 overflow-hidden">
                  <div 
                    className="h-full bg-orange-500 transition-all duration-500"
                    style={{ width: totalAssets > 0 ? `${(urgentAssets / totalAssets) * 100}%` : '0%' }}
                  />
                </div>
              </div>
              
              <div className="p-4 rounded-xl bg-red-50 border border-red-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <XCircle className="w-6 h-6 text-red-500" />
                    <span className="text-base font-medium text-slate-700">منتهية</span>
                  </div>
                  <span className="text-2xl font-bold number text-red-600">{expiredAssets}</span>
                </div>
                <div className="h-3 rounded-full bg-red-100 overflow-hidden">
                  <div 
                    className="h-full bg-red-500 transition-all duration-500"
                    style={{ width: totalAssets > 0 ? `${(expiredAssets / totalAssets) * 100}%` : '0%' }}
                  />
                </div>
              </div>
            </div>

            {/* Combined Bar */}
            <div className="mt-6 pt-4 border-t">
              <p className="text-sm text-slate-500 mb-3">التوزيع العام</p>
              <div className="h-5 rounded-full overflow-hidden flex" style={{ backgroundColor: '#e2e8f0' }}>
                {totalAssets > 0 && (
                  <>
                    <div className="h-full bg-emerald-500" style={{ width: `${(activeItems / totalAssets) * 100}%` }} />
                    <div className="h-full bg-amber-500" style={{ width: `${(upcomingAssets / totalAssets) * 100}%` }} />
                    <div className="h-full bg-red-500" style={{ width: `${(urgentAssets / totalAssets) * 100}%` }} />
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expiring Items - Full Width */}
      <Card className="border-0 shadow-sm" data-testid="expiring-items-card">
        <CardHeader className="pb-3 pt-4 px-6">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg" style={{ color: '#1a365d' }}>
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              التنبيهات والمواعيد القريبة
            </CardTitle>
            <Link to="/notifications">
              <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700">
                عرض الكل
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent className="px-6 pb-6">
          {expiringItems.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Calendar className="w-16 h-16 mx-auto mb-3 opacity-50" />
              <p className="text-lg">لا توجد تنبيهات</p>
              <p className="text-sm mt-1">جميع الأصول سارية</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {expiringItems.slice(0, 9).map((item, index) => {
                const days = getDaysRemaining(item.expiry_date);
                return (
                  <div
                    key={`${item.item_id}-${item.field}`}
                    className={`flex items-center gap-3 p-4 rounded-xl border ${days <= 7 ? 'bg-red-50 border-red-200' : days <= 14 ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}
                    data-testid={`expiring-item-${index}`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${days <= 7 ? 'bg-red-100' : days <= 14 ? 'bg-amber-100' : 'bg-blue-100'}`}>
                      <AlertTriangle className={`w-6 h-6 ${days <= 7 ? 'text-red-500' : days <= 14 ? 'text-amber-500' : 'text-blue-500'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{item.item}</p>
                      <p className="text-sm text-slate-500">{item.field}</p>
                    </div>
                    <div className="text-left flex-shrink-0">
                      {getStatusBadge(days)}
                      <p className="text-sm number mt-1" style={{ color: '#1a365d' }}>{days > 0 ? `${days} يوم` : 'منتهي'}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notifications Alert */}
      {summary?.unread_notifications > 0 && (
        <Card className="border-0 shadow-sm overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a365d 0%, #2d4a7c 100%)' }}>
          <CardContent className="p-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <Bell className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-white text-sm">إشعارات غير مقروءة</h3>
                <p className="text-white/70 text-xs">لديك {summary.unread_notifications} إشعار جديد</p>
              </div>
              <Link to="/notifications">
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white text-xs">عرض</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
